import React from "react";

/**
 * Индивидуальные иконки достижений Рублокса — по ключу из каталога бэка
 * (routs/RubloxAchievs.py). Рисованные inline-SVG (без эмодзи), цветные,
 * с градиентами. Незаработанные плитки обесцвечивают иконку CSS-фильтром
 * (см. .rb-up__achiev-icon.is-gray в user-profile-page.css).
 *
 * Ид-ы градиентов префиксованы "ra-<key>" — на странице каждая иконка
 * встречается максимум дважды (плитка + модалка), дубль id безопасен:
 * браузер берёт первый, а определения идентичны.
 */

/* Мини-хелперы палитры — единый мультяшный стиль */
const VIOLET = ["#8b5cf6", "#6d28d9"];
const BLUE = ["#60a5fa", "#3357ff"];
const GOLD = ["#fde047", "#f59e0b"];
const PINK = ["#f9a8d4", "#ec4899"];
const RED = ["#fb7185", "#e11d48"];
const GREEN = ["#4ade80", "#16a34a"];
const CYAN = ["#67e8f9", "#06b6d4"];

function Grad({ id, c, x1 = 0, y1 = 0, x2 = 0, y2 = 1 }) {
  return (
    <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2}>
      <stop offset="0" stopColor={c[0]} />
      <stop offset="1" stopColor={c[1]} />
    </linearGradient>
  );
}

/** Звёздочка-искра в точке (cx, cy) радиуса r. */
function Spark({ cx, cy, r, fill = "#fde047" }) {
  return (
    <path
      d={`M${cx} ${cy - r} L${cx + r * 0.3} ${cy - r * 0.3} L${cx + r} ${cy} L${cx + r * 0.3} ${cy + r * 0.3} L${cx} ${cy + r} L${cx - r * 0.3} ${cy + r * 0.3} L${cx - r} ${cy} L${cx - r * 0.3} ${cy - r * 0.3} Z`}
      fill={fill}
    />
  );
}

/* ── 27 иконок по ключам каталога ─────────────────────────────── */
const ICONS = {
  /* Первый шаг — геймпад с золотой звёздочкой */
  first_game: () => (
    <>
      <defs><Grad id="ra-fg" c={VIOLET} /></defs>
      <rect x="6" y="16" width="36" height="20" rx="10" fill="url(#ra-fg)" />
      <path d="M15 22v8M11 26h8" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
      <circle cx="32" cy="23" r="2.4" fill="#fde047" />
      <circle cx="37" cy="28" r="2.4" fill="#4ade80" />
      <Spark cx="38" cy="11" r="5" />
    </>
  ),
  /* Геймдизайнер — куб и карандаш */
  games_5: () => (
    <>
      <defs><Grad id="ra-g5" c={BLUE} /><Grad id="ra-g5p" c={GOLD} /></defs>
      <path d="M10 18l12-6 12 6v14l-12 6-12-6Z" fill="url(#ra-g5)" />
      <path d="M10 18l12 6 12-6M22 24v14" stroke="#fff" strokeWidth="2" fill="none" opacity="0.8" />
      <path d="M30 34 40 24l4 4-10 10-5.4 1.4Z" fill="url(#ra-g5p)" stroke="#b45309" strokeWidth="1.4" />
    </>
  ),
  /* Мастерская — молоток и отвёртка крест-накрест */
  games_10: () => (
    <>
      <defs><Grad id="ra-g10" c={["#94a3b8", "#475569"]} /></defs>
      <g transform="rotate(45 24 24)">
        <rect x="21.4" y="11" width="5.2" height="26" rx="2.4" fill="#f59e0b" />
        <rect x="14" y="4" width="20" height="8.5" rx="3" fill="url(#ra-g10)" />
      </g>
      <g transform="rotate(-45 24 24)">
        <rect x="21.6" y="5" width="4.8" height="14" rx="1.6" fill="#cbd5e1" />
        <rect x="19.6" y="19" width="8.8" height="17" rx="4" fill="#e11d48" />
      </g>
    </>
  ),
  /* Фабрика игр — заводик с кубиками */
  games_25: () => (
    <>
      <defs><Grad id="ra-g25" c={VIOLET} /><Grad id="ra-g25b" c={PINK} /></defs>
      <path d="M8 40V22l10 6v-6l10 6v-6l12-4v22Z" fill="url(#ra-g25)" />
      <rect x="12" y="10" width="6" height="12" rx="1.5" fill="url(#ra-g25b)" />
      <circle cx="15" cy="7" r="3.4" fill="#e2e8f0" />
      <circle cx="21" cy="5" r="2.4" fill="#e2e8f0" opacity="0.8" />
      <rect x="14" y="31" width="5" height="5" rx="1" fill="#fde047" />
      <rect x="23" y="31" width="5" height="5" rx="1" fill="#4ade80" />
      <rect x="32" y="31" width="5" height="5" rx="1" fill="#67e8f9" />
    </>
  ),
  /* Первые зрители — экран с play и зрители */
  plays_10: () => (
    <>
      <defs><Grad id="ra-p10" c={BLUE} /></defs>
      <rect x="8" y="8" width="32" height="22" rx="4" fill="url(#ra-p10)" />
      <path d="M21 13.5v11l9.5-5.5Z" fill="#fff" />
      <circle cx="16" cy="38" r="4.5" fill="#f9a8d4" />
      <circle cx="27" cy="38" r="4.5" fill="#fde047" />
      <circle cx="38" cy="38" r="4.5" fill="#4ade80" />
    </>
  ),
  /* Публика собирается — три разноцветных зрителя */
  plays_100: () => (
    <>
      <defs><Grad id="ra-p100" c={VIOLET} /></defs>
      <circle cx="24" cy="15" r="7" fill="url(#ra-p100)" />
      <path d="M13 36a11 11 0 0 1 22 0v3H13Z" fill="url(#ra-p100)" />
      <circle cx="10" cy="18" r="5" fill="#38bdf8" />
      <path d="M2.5 34a8 8 0 0 1 15 0v3h-15Z" fill="#38bdf8" />
      <circle cx="38" cy="18" r="5" fill="#fb7185" />
      <path d="M30.5 34a8 8 0 0 1 15 0v3h-15Z" fill="#fb7185" />
    </>
  ),
  /* Звезда Рублокса — золотая звезда с лучами */
  plays_1000: () => (
    <>
      <defs><Grad id="ra-p1k" c={GOLD} /></defs>
      <path d="M24 5l5.4 11.4L42 18.2l-9 8.9 2.1 12.4L24 33.6l-11.1 5.9L15 27.1l-9-8.9 12.6-1.8Z"
            fill="url(#ra-p1k)" stroke="#b45309" strokeWidth="1.6" strokeLinejoin="round" />
      <Spark cx="8" cy="9" r="3.4" />
      <Spark cx="41" cy="38" r="3" fill="#fb7185" />
      <Spark cx="42" cy="7" r="2.6" fill="#67e8f9" />
    </>
  ),
  /* Нравится людям — сердце с бликом */
  likes_10: () => (
    <>
      <defs><Grad id="ra-l10" c={RED} /></defs>
      <path d="M24 41C10 31.5 5 24.5 5 17.5A9.5 9.5 0 0 1 24 14a9.5 9.5 0 0 1 19 3.5c0 7-5 14-19 23.5Z"
            fill="url(#ra-l10)" />
      <path d="M13 15a5.5 5.5 0 0 1 5-4" stroke="#fff" strokeWidth="2.6"
            strokeLinecap="round" fill="none" opacity="0.85" />
    </>
  ),
  /* Любимец публики — сердце в короне */
  likes_50: () => (
    <>
      <defs><Grad id="ra-l50" c={RED} /><Grad id="ra-l50c" c={GOLD} /></defs>
      <path d="M24 43C12 35 7.5 29 7.5 23A8 8 0 0 1 24 20a8 8 0 0 1 16.5 3c0 6-4.5 12-16.5 20Z"
            fill="url(#ra-l50)" />
      <path d="M13 14 15.5 5l5.5 5 3-6 3 6 5.5-5L35 14Z" fill="url(#ra-l50c)" stroke="#b45309" strokeWidth="1.2" />
    </>
  ),
  /* Не один — два смайлика */
  friend_1: () => (
    <>
      <defs><Grad id="ra-f1" c={GOLD} /><Grad id="ra-f1b" c={CYAN} /></defs>
      <circle cx="17" cy="22" r="12" fill="url(#ra-f1)" />
      <circle cx="13.5" cy="19" r="1.8" fill="#78350f" />
      <circle cx="20.5" cy="19" r="1.8" fill="#78350f" />
      <path d="M12.5 26a6 6 0 0 0 9 0" stroke="#78350f" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="33" cy="28" r="10" fill="url(#ra-f1b)" />
      <circle cx="30" cy="26" r="1.6" fill="#0e4a5e" />
      <circle cx="36" cy="26" r="1.6" fill="#0e4a5e" />
      <path d="M29.5 31.5a5 5 0 0 0 7 0" stroke="#0e4a5e" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    </>
  ),
  /* Компания — трое друзей */
  friends_5: () => (
    <>
      <defs><Grad id="ra-f5" c={VIOLET} /></defs>
      <circle cx="24" cy="13" r="6.5" fill="url(#ra-f5)" />
      <path d="M14 33a10 10 0 0 1 20 0v4H14Z" fill="url(#ra-f5)" />
      <circle cx="9.5" cy="17" r="5" fill="#fb7185" />
      <path d="M2 33.5a7.5 7.5 0 0 1 13.6-4.2A12 12 0 0 0 12 37H2Z" fill="#fb7185" />
      <circle cx="38.5" cy="17" r="5" fill="#38bdf8" />
      <path d="M46 33.5a7.5 7.5 0 0 0-13.6-4.2A12 12 0 0 1 36 37h10Z" fill="#38bdf8" />
    </>
  ),
  /* Душа компании — весельчак и конфетти */
  friends_20: () => (
    <>
      <defs><Grad id="ra-f20" c={GOLD} /></defs>
      <circle cx="24" cy="24" r="13" fill="url(#ra-f20)" />
      <circle cx="19" cy="21" r="2" fill="#78350f" />
      <circle cx="29" cy="21" r="2" fill="#78350f" />
      <path d="M17 27a8.5 8.5 0 0 0 14 0" stroke="#78350f" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <Spark cx="8" cy="10" r="3.4" fill="#fb7185" />
      <Spark cx="40" cy="9" r="3" fill="#67e8f9" />
      <Spark cx="42" cy="36" r="2.8" fill="#4ade80" />
      <Spark cx="7" cy="39" r="2.6" fill="#a78bfa" />
    </>
  ),
  /* Щедрость — подарок с бантом */
  gift_sent: () => (
    <>
      <defs><Grad id="ra-gs" c={RED} /><Grad id="ra-gsl" c={GOLD} /></defs>
      <rect x="8" y="20" width="32" height="21" rx="3" fill="url(#ra-gs)" />
      <rect x="7" y="13" width="34" height="8" rx="2.5" fill="#fb7185" />
      <rect x="21.5" y="13" width="5" height="28" fill="url(#ra-gsl)" />
      <path d="M24 13c-4-6-11-5-10.4-1.4.4 2.7 6 2.3 10.4 1.4Zm0 0c4-6 11-5 10.4-1.4-.4 2.7-6 2.3-10.4 1.4Z"
            fill="url(#ra-gsl)" stroke="#b45309" strokeWidth="1" />
    </>
  ),
  /* Добрый волшебник — волшебная палочка */
  gifts_sent_10: () => (
    <>
      <defs><Grad id="ra-gs10" c={VIOLET} /></defs>
      <rect x="7" y="33" width="26" height="6" rx="3" fill="url(#ra-gs10)" transform="rotate(-38 20 36)" />
      <Spark cx="36" cy="12" r="8" />
      <Spark cx="24" cy="7" r="3" fill="#67e8f9" />
      <Spark cx="43" cy="24" r="3" fill="#fb7185" />
      <Spark cx="30" cy="26" r="2.4" fill="#a78bfa" />
    </>
  ),
  /* Популярность — коробка с сердечками */
  gifts_got_5: () => (
    <>
      <defs><Grad id="ra-gg5" c={CYAN} /></defs>
      <path d="M9 24h30v16a2.5 2.5 0 0 1-2.5 2.5h-25A2.5 2.5 0 0 1 9 40Z" fill="url(#ra-gg5)" />
      <path d="M6 18h36v6H6Z" fill="#38bdf8" />
      <path d="M17 9c2.8 0 5 2.2 5 5 0 3.5-5 7-5 7s-5-3.5-5-7c0-2.8 2.2-5 5-5Z"
            fill="#fb7185" transform="translate(-1 -4) scale(0.72)" />
      <path d="M31 6c2.8 0 5 2.2 5 5 0 3.5-5 7-5 7s-5-3.5-5-7c0-2.8 2.2-5 5-5Z"
            fill="#f43f5e" transform="translate(11 -1) scale(0.62)" />
    </>
  ),
  /* Копилка — свинка с монеткой */
  diamonds_100: () => (
    <>
      <defs><Grad id="ra-d100" c={PINK} /><Grad id="ra-d100c" c={GOLD} /></defs>
      <ellipse cx="24" cy="28" rx="16" ry="12" fill="url(#ra-d100)" />
      <rect x="20" y="13" width="8" height="2.6" rx="1.3" fill="#9d174d" />
      <circle cx="9" cy="26" r="3.5" fill="#f9a8d4" />
      <circle cx="7.8" cy="25.4" r="1" fill="#9d174d" />
      <circle cx="16" cy="24" r="1.6" fill="#9d174d" />
      <path d="M12 38l1.4 4M34 38l1.4 4" stroke="#ec4899" strokeWidth="3.4" strokeLinecap="round" />
      <circle cx="24" cy="9" r="5.5" fill="url(#ra-d100c)" stroke="#b45309" strokeWidth="1.2" />
      <path d="M24 6.4v5.2M21.8 9h4.4" stroke="#78350f" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  /* Сокровищница — сундук с алмазом */
  diamonds_1000: () => (
    <>
      <defs><Grad id="ra-d1k" c={["#d97706", "#92400e"]} /><Grad id="ra-d1kg" c={CYAN} /></defs>
      <path d="M7 20a8 8 0 0 1 8-8h18a8 8 0 0 1 8 8v3H7Z" fill="url(#ra-d1k)" />
      <rect x="7" y="23" width="34" height="17" rx="3" fill="#b45309" />
      <rect x="20.5" y="20" width="7" height="10" rx="2" fill="#fde047" stroke="#b45309" strokeWidth="1.2" />
      <path d="M24 6l4 4-4 4-4-4Z" fill="url(#ra-d1kg)" />
      <Spark cx="38" cy="8" r="3" />
      <Spark cx="10" cy="8" r="2.4" fill="#67e8f9" />
    </>
  ),
  /* Стиль — рамка со звездой */
  frame_1: () => (
    <>
      <defs><Grad id="ra-fr1" c={GOLD} /></defs>
      <rect x="8" y="8" width="32" height="32" rx="6" fill="none" stroke="url(#ra-fr1)" strokeWidth="6" />
      <Spark cx="24" cy="24" r="7" fill="#a78bfa" />
    </>
  ),
  /* Коллекционер — стопка рамок */
  frames_5: () => (
    <>
      <defs><Grad id="ra-fr5" c={VIOLET} /></defs>
      <rect x="16" y="16" width="26" height="26" rx="5" fill="none" stroke="#f9a8d4" strokeWidth="4.6" />
      <rect x="11" y="11" width="26" height="26" rx="5" fill="none" stroke="#67e8f9" strokeWidth="4.6" />
      <rect x="6" y="6" width="26" height="26" rx="5" fill="none" stroke="url(#ra-fr5)" strokeWidth="4.6" />
    </>
  ),
  /* Наводим красоту — кисть с мазком */
  cover_set: () => (
    <>
      <defs><Grad id="ra-cs" c={PINK} /><Grad id="ra-csh" c={["#a16207", "#713f12"]} /></defs>
      <path d="M6 41c6 1.5 10-1 11.5-5.5L12 30C7.5 31.5 5.5 35 6 41Z" fill="#e2e8f0" />
      <rect x="14" y="10" width="7" height="22" rx="3" fill="url(#ra-csh)" transform="rotate(-45 20 22)" />
      <path d="M28 26c6-2 10-7 11-15-4 1-12 3-16 10" fill="url(#ra-cs)" />
      <path d="M30 34c5 0 9 2 12 6-4 2-9 2-13-1" fill="#a78bfa" />
    </>
  ),
  /* Яркое имя — радужная буква А */
  color_set: () => (
    <>
      <defs>
        <linearGradient id="ra-cl" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fb7185" />
          <stop offset="0.5" stopColor="#a78bfa" />
          <stop offset="1" stopColor="#38bdf8" />
        </linearGradient>
      </defs>
      <path d="M24 6 38 42h-7l-2.4-7H19.4L17 42h-7Zm0 12-3.4 10h6.8Z"
            fill="url(#ra-cl)" />
      <Spark cx="40" cy="10" r="4" />
    </>
  ),
  /* Визитка — карточка с аватаром */
  profile_full: () => (
    <>
      <defs><Grad id="ra-pf" c={BLUE} /></defs>
      <rect x="5" y="10" width="38" height="28" rx="5" fill="#fff" stroke="url(#ra-pf)" strokeWidth="3" />
      <circle cx="16" cy="21" r="5" fill="url(#ra-pf)" />
      <path d="M9 32a7 7 0 0 1 14 0" fill="url(#ra-pf)" />
      <rect x="26" y="17" width="13" height="3.4" rx="1.7" fill="#a78bfa" />
      <rect x="26" y="23" width="13" height="3.4" rx="1.7" fill="#cbd5e1" />
      <rect x="26" y="29" width="9" height="3.4" rx="1.7" fill="#cbd5e1" />
    </>
  ),
  /* Экскурсия — компас */
  tour_done: () => (
    <>
      <defs><Grad id="ra-td" c={BLUE} /></defs>
      <circle cx="24" cy="24" r="17" fill="url(#ra-td)" />
      <circle cx="24" cy="24" r="13" fill="#eff6ff" />
      <path d="M24 13l4 9-4 13-4-13Z" fill="#e11d48" />
      <path d="M24 13l4 9h-8Z" fill="#fb7185" />
      <circle cx="24" cy="24" r="2.2" fill="#1e293b" />
    </>
  ),
  /* Прокачка — ступеньки вверх со стрелкой */
  level_1: () => (
    <>
      <defs><Grad id="ra-l1" c={GREEN} /></defs>
      <path d="M6 42v-8h9v-8h9v-8h9V10h9" fill="none" stroke="url(#ra-l1)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M33 5h10v10" fill="none" stroke="url(#ra-l1)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <Spark cx="12" cy="16" r="3.4" />
    </>
  ),
  /* Максимум — золотой кубок */
  level_5: () => (
    <>
      <defs><Grad id="ra-l5" c={GOLD} /></defs>
      <path d="M14 7h20v9a10 10 0 0 1-20 0Z" fill="url(#ra-l5)" />
      <path d="M14 9H7a7 7 0 0 0 8 8M34 9h7a7 7 0 0 1-8 8" fill="none" stroke="#f59e0b" strokeWidth="3.4" />
      <rect x="21" y="25" width="6" height="8" fill="#f59e0b" />
      <rect x="14" y="33" width="20" height="6" rx="2" fill="url(#ra-l5)" stroke="#b45309" strokeWidth="1.2" />
      <path d="M19 11a6 6 0 0 1 4-3" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" fill="none" opacity="0.8" />
    </>
  ),
  /* Старожил — календарь с галочкой */
  veteran_30: () => (
    <>
      <defs><Grad id="ra-v30" c={RED} /><Grad id="ra-v30g" c={GREEN} /></defs>
      <rect x="7" y="10" width="34" height="31" rx="5" fill="#fff" stroke="#cbd5e1" strokeWidth="2" />
      <path d="M7 15a5 5 0 0 1 5-5h24a5 5 0 0 1 5 5v6H7Z" fill="url(#ra-v30)" />
      <rect x="14" y="5" width="4" height="9" rx="2" fill="#9f1239" />
      <rect x="30" y="5" width="4" height="9" rx="2" fill="#9f1239" />
      <path d="M16 31l5.5 5.5L32.5 26" fill="none" stroke="url(#ra-v30g)" strokeWidth="4.4" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  /* Ветеран — щит со звездой */
  veteran_180: () => (
    <>
      <defs><Grad id="ra-v180" c={VIOLET} /><Grad id="ra-v180s" c={GOLD} /></defs>
      <path d="M24 4l16 6v12c0 10-6.5 17.5-16 22-9.5-4.5-16-12-16-22V10Z" fill="url(#ra-v180)" />
      <path d="M24 9l11.8 4.4V22c0 7.4-4.8 13-11.8 16.6C17 35 12.2 29.4 12.2 22v-8.6Z"
            fill="#fff" opacity="0.14" />
      <path d="M24 14l2.9 6 6.6 1-4.8 4.6 1.2 6.6L24 29l-5.9 3.2 1.2-6.6-4.8-4.6 6.6-1Z"
            fill="url(#ra-v180s)" />
    </>
  ),
};

/* Фолбэк — медаль (если бэк добавит новую ачивку раньше фронта) */
function FallbackMedal() {
  return (
    <>
      <defs><Grad id="ra-fb" c={GOLD} /></defs>
      <path d="M17 5h6l3 12-6 2-6-12Z" fill="#3357ff" />
      <path d="M31 5h-6l-3 12 6 2 6-12Z" fill="#e11d48" />
      <circle cx="24" cy="30" r="12" fill="url(#ra-fb)" stroke="#b45309" strokeWidth="1.6" />
      <path d="M24 23l2.2 4.4 4.8.7-3.5 3.4.8 4.8-4.3-2.3-4.3 2.3.8-4.8-3.5-3.4 4.8-.7Z" fill="#fff7ed" />
    </>
  );
}

export default function AchievIcon({ achKey, size = 32 }) {
  const Icon = ICONS[achKey] || FallbackMedal;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <Icon />
    </svg>
  );
}
